import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop, { centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    IconBrightness,
    IconCheck,
    IconCopy,
    IconCrop,
    IconEye,
    IconGripVertical,
    IconRestore,
    IconRotateClockwise,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { createToolItemId } from '../../toolPageUtils';
import { renderPdfPageThumbnails } from '../../pdf/shared/pdfPreviewEngine';
import { renderPdfPagePreview } from '../../pdf/crop/cropPdfEngine';
import { pageHasColorAdjust, normalizeColorAdjust, DEFAULT_COLOR_ADJUST, buildColorFilterCss } from '../../pdf/shared/pdfPageEditEngine';
import PdfPageThumb from './PdfPageThumb';
import PdfPageColorPanel from './PdfPageColorPanel';
import PerspectiveQuadCrop, {
    cornersToRegion,
    defaultNormalizedCorners,
} from './PerspectiveQuadCrop';
import { buildFlattenPreviewUrl, flattenCornersKey } from '../../pdf/shared/flattenPreview';
import { fitImageStyle, useBodyScrollLock, useFitBox } from '../image/useFullscreenStage';

function percentCropToRegion(percentCrop) {
    if (!percentCrop?.width || !percentCrop?.height) return null;
    return {
        left: percentCrop.x / 100,
        top: percentCrop.y / 100,
        width: percentCrop.width / 100,
        height: percentCrop.height / 100,
    };
}

function PageCard({
    page,
    thumb,
    flattenedSrc,
    labels,
    selectable,
    selected,
    onToggleSelect,
    onView,
    onRotateRight,
    onCrop,
    onColor,
    onDuplicate,
    onToggleRemoved,
}) {
    const { removed, cropRegion, cropCorners, cropMode, rotation, brightness, contrast, saturation, grayscale, sepia } = page;
    const hasColor = pageHasColorAdjust(page);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: page.id });

    const ringClass = removed
        ? 'border-[#FFB7B1] ring-2 ring-[#FFB7B1]/35 dark:border-red-400 dark:ring-red-400/25'
        : (selectable && selected) || cropRegion || cropCorners || hasColor
            ? 'border-pdf-cat-solid ring-2 ring-pdf-cat-solid/20 dark:border-orange-500 dark:ring-orange-500/20'
            : 'border-[#ECE8E4] dark:border-zinc-700';

    const handleBodyClick = () => {
        if (selectable && !removed) onToggleSelect(page.id);
    };

    const cropBadge = cropMode === 'flatten' ? labels.flattened : labels.cropped;

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
            className="group relative"
        >
            <div className={`w-full overflow-hidden rounded-xl border bg-white text-left shadow-[0_4px_12px_rgba(28,24,21,0.06)] transition-colors dark:bg-zinc-900 ${ringClass}`}>
                <div
                    className={`relative overflow-hidden ${selectable && !removed ? 'cursor-pointer' : ''}`}
                    role={selectable && !removed ? 'button' : undefined}
                    tabIndex={selectable && !removed ? 0 : undefined}
                    onClick={handleBodyClick}
                    onKeyDown={(e) => {
                        if (selectable && !removed && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            onToggleSelect(page.id);
                        }
                    }}
                >
                    <PdfPageThumb
                        src={thumb}
                        alt={page.label}
                        cropRegion={cropMode === 'flatten' ? null : cropRegion}
                        cropCorners={cropMode === 'flatten' ? cropCorners : null}
                        flattenedSrc={cropMode === 'flatten' ? flattenedSrc : null}
                        rotation={rotation}
                        removed={removed}
                        brightness={brightness}
                        contrast={contrast}
                        saturation={saturation}
                        grayscale={grayscale}
                        sepia={sepia}
                    />

                    <div className="pointer-events-none absolute inset-0 bg-[#1C1815]/0 transition-colors group-hover:bg-[#1C1815]/10" />

                    {!removed ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onView(page); }}
                            className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#3D3833] opacity-0 shadow-[0_4px_14px_rgba(28,24,21,0.25)] transition-opacity hover:bg-white group-hover:pointer-events-auto group-hover:opacity-100 dark:bg-zinc-900/95 dark:text-zinc-100"
                            aria-label={labels.view}
                            title={labels.view}
                        >
                            <IconEye size={26} stroke={1.7} />
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="absolute right-2 top-2 z-10 flex h-9 w-9 cursor-grab items-center justify-center rounded-lg border border-[#ECE8E4] bg-white/95 text-[#6B635C] opacity-0 shadow-sm transition-opacity group-hover:opacity-100 active:cursor-grabbing dark:border-zinc-600 dark:bg-zinc-900/95 dark:text-zinc-300"
                        aria-label={labels.drag}
                        title={labels.drag}
                        onClick={(e) => e.stopPropagation()}
                        {...attributes}
                        {...listeners}
                    >
                        <IconGripVertical size={18} stroke={2} />
                    </button>

                    {removed ? (
                        <span className="absolute left-2 top-2 z-10 rounded-full bg-[#FF8D86] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.02em] text-white shadow-sm">
                            {labels.removed}
                        </span>
                    ) : selectable && selected ? (
                        <span className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-pdf-cat-solid text-white shadow-sm">
                            <IconCheck size={16} stroke={2.6} />
                        </span>
                    ) : null}

                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-2 bg-gradient-to-t from-[#1C1815]/75 to-transparent px-2 pb-3 pt-10 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
                        {removed ? (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onToggleRemoved(page.id); }}
                                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#FFB7B1] px-3 text-[12px] font-semibold text-[#A33833] shadow-sm transition-colors hover:bg-[#FFA49C] dark:bg-red-300 dark:text-red-950"
                                aria-label={labels.restore}
                                title={labels.restore}
                            >
                                <IconRestore size={18} stroke={1.9} />
                                {labels.restore}
                            </button>
                        ) : (
                            <>
                                {/* One rotate button: four clockwise turns cover every angle. */}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onRotateRight(page.id); }}
                                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#3D3833] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200"
                                    aria-label={labels.rotateRight}
                                    title={labels.rotateRight}
                                >
                                    <IconRotateClockwise size={20} stroke={1.8} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onCrop(page); }}
                                    className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm transition-colors ${
                                        cropRegion || cropCorners
                                            ? 'bg-pdf-cat-solid text-white hover:bg-pdf-cat-solid/90'
                                            : 'bg-white/95 text-[#3D3833] hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200'
                                    }`}
                                    aria-label={labels.crop}
                                    title={labels.crop}
                                >
                                    <IconCrop size={20} stroke={1.8} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onColor(page); }}
                                    className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm transition-colors ${
                                        hasColor
                                            ? 'bg-pdf-cat-solid text-white hover:bg-pdf-cat-solid/90'
                                            : 'bg-white/95 text-[#3D3833] hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200'
                                    }`}
                                    aria-label={labels.color}
                                    title={labels.color}
                                >
                                    <IconBrightness size={20} stroke={1.8} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onDuplicate(page.id); }}
                                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#3D3833] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200"
                                    aria-label={labels.duplicate}
                                    title={labels.duplicate}
                                >
                                    <IconCopy size={20} stroke={1.8} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onToggleRemoved(page.id); }}
                                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#D33A2C] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95"
                                    aria-label={labels.delete}
                                    title={labels.delete}
                                >
                                    <IconTrash size={20} stroke={1.8} />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-[#ECE8E4] px-3 py-2.5 dark:border-zinc-700">
                    <p className={`truncate text-[13px] font-semibold ${removed ? 'text-[#FF8D86] dark:text-red-300' : 'text-[#3D3833] dark:text-zinc-200'}`}>
                        {page.label}
                    </p>
                    {!removed && (cropRegion || cropCorners || hasColor) ? (
                        <div className="flex flex-none flex-wrap justify-end gap-1">
                            {(cropRegion || cropCorners) ? (
                                <span className="rounded-full bg-pdf-cat-tint px-2.5 py-0.5 text-[11px] font-semibold text-pdf-cat-solid dark:bg-orange-950/40 dark:text-orange-300">
                                    {cropBadge}
                                </span>
                            ) : null}
                            {hasColor ? (
                                <span className="rounded-full bg-pdf-cat-tint px-2.5 py-0.5 text-[11px] font-semibold text-pdf-cat-solid dark:bg-orange-950/40 dark:text-orange-300">
                                    {labels.colorAdjusted}
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/**
 * Grid of a single PDF's pages as editable image blocks: view, rotate, crop,
 * duplicate, delete/undelete, drag-reorder. Optional click-to-select mode.
 */
export default function PdfPageEditGrid({
    file,
    pages,
    onPagesChange,
    thumbCache,
    selectable = false,
    isSelected,
    onToggleSelect,
}) {
    const { t } = useTranslation();
    const imgRef = useRef(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    const thumbKey = (pageIndex) => `${fileKey}-${pageIndex}`;

    const [thumbs, setThumbs] = useState(() => {
        const seed = {};
        if (thumbCache) {
            for (const page of pages) {
                const key = thumbKey(page.pageIndex);
                if (thumbCache.has(key)) seed[page.pageIndex] = thumbCache.get(key);
            }
        }
        return seed;
    });

    const [modalPage, setModalPage] = useState(null);
    const [modalMode, setModalMode] = useState('crop'); // 'crop' | 'view' | 'color'
    const [modalSrc, setModalSrc] = useState('');
    const [loadingModal, setLoadingModal] = useState(false);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [saveMode, setSaveMode] = useState('crop'); // 'crop' | 'flatten'
    const [flattenCorners, setFlattenCorners] = useState(null);
    const [flattenPreviews, setFlattenPreviews] = useState({});
    const flattenPreviewUrlsRef = useRef({});
    const [draftColor, setDraftColor] = useState(DEFAULT_COLOR_ADJUST);

    // The page modal is full screen (same shape as the image crop modals): the
    // page is fitted to a measured stage, so every crop handle stays in view and
    // nothing scrolls under a drag.
    const stageRef = useRef(null);
    const fitBox = useFitBox(stageRef, !!modalPage);
    useBodyScrollLock(!!modalPage);

    // No backdrop to click now that it fills the screen — Escape closes it.
    useEffect(() => {
        if (!modalPage) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setModalPage(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [modalPage]);

    useEffect(() => {
        let cancelled = false;

        async function loadThumbs() {
            const missing = pages
                .map((page) => page.pageIndex)
                .filter((pageIndex) => !(pageIndex in thumbs));
            if (!missing.length) return;

            try {
                const rendered = await renderPdfPageThumbnails(file, {
                    maxPages: Math.max(...missing) + 1,
                    thumbHeight: 420,
                });
                if (cancelled) return;
                setThumbs((prev) => {
                    const next = { ...prev };
                    for (const entry of rendered) {
                        const pageIndex = entry.pageNumber - 1;
                        next[pageIndex] = entry.src;
                        thumbCache?.set(thumbKey(pageIndex), entry.src);
                    }
                    return next;
                });
            } catch (err) {
                if (cancelled) return;
                const msg = err?.message === 'password'
                    ? t('pdfPageEditor.password')
                    : t('pdfPageEditor.previewFailed');
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            }
        }

        loadThumbs();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, pages]);

    // Build perspective-warped thumbnails for flatten pages so the grid shows the result.
    useEffect(() => {
        let cancelled = false;

        async function syncFlattenPreviews() {
            const needed = {};
            for (const page of pages) {
                if (page.cropMode !== 'flatten' || page.cropCorners?.length !== 4) continue;
                const src = thumbs[page.pageIndex];
                if (!src) continue;
                needed[page.id] = {
                    src,
                    key: `${src}|${flattenCornersKey(page.cropCorners)}`,
                    corners: page.cropCorners,
                };
            }

            const nextUrls = { ...flattenPreviewUrlsRef.current };

            // Drop unused
            for (const id of Object.keys(nextUrls)) {
                if (!needed[id] || nextUrls[id].key !== needed[id].key) {
                    URL.revokeObjectURL(nextUrls[id].url);
                    delete nextUrls[id];
                }
            }

            const jobs = [];
            for (const [id, meta] of Object.entries(needed)) {
                if (nextUrls[id]?.key === meta.key) continue;
                jobs.push((async () => {
                    try {
                        const url = await buildFlattenPreviewUrl(meta.src, meta.corners);
                        if (!url || cancelled) {
                            if (url) URL.revokeObjectURL(url);
                            return null;
                        }
                        return { id, url, key: meta.key };
                    } catch {
                        return null;
                    }
                })());
            }

            const results = jobs.length ? await Promise.all(jobs) : [];
            if (cancelled) {
                for (const r of results) {
                    if (r?.url) URL.revokeObjectURL(r.url);
                }
                return;
            }

            for (const r of results) {
                if (!r) continue;
                if (nextUrls[r.id]) URL.revokeObjectURL(nextUrls[r.id].url);
                nextUrls[r.id] = { url: r.url, key: r.key };
            }

            flattenPreviewUrlsRef.current = nextUrls;
            setFlattenPreviews(
                Object.fromEntries(Object.entries(nextUrls).map(([id, entry]) => [id, entry.url])),
            );
        }

        syncFlattenPreviews();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, thumbs]);

    useEffect(() => () => {
        for (const entry of Object.values(flattenPreviewUrlsRef.current)) {
            URL.revokeObjectURL(entry.url);
        }
        flattenPreviewUrlsRef.current = {};
    }, []);

    useEffect(() => {
        if (!modalPage) return undefined;

        let cancelled = false;
        setLoadingModal(true);
        setModalSrc('');
        setCrop(undefined);
        setCompletedCrop(null);

        renderPdfPagePreview(file, modalPage.pageIndex + 1)
            .then((src) => { if (!cancelled) setModalSrc(src); })
            .catch((err) => {
                if (cancelled) return;
                const msg = err?.message === 'password'
                    ? t('pdfPageEditor.password')
                    : t('pdfPageEditor.previewFailed');
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                setModalPage(null);
            })
            .finally(() => { if (!cancelled) setLoadingModal(false); });

        return () => { cancelled = true; };
    }, [file, modalPage, t]);

    const labels = useMemo(() => ({
        view: t('pdfPageEditor.view'),
        rotateRight: t('pdfPageEditor.rotateRight'),
        crop: t('pdfPageEditor.crop'),
        cropped: t('pdfPageEditor.cropped'),
        flattened: t('pdfPageEditor.flattened'),
        color: t('pdfPageEditor.color'),
        colorAdjusted: t('pdfPageEditor.colorAdjusted'),
        duplicate: t('pdfPageEditor.duplicate'),
        delete: t('pdfPageEditor.delete'),
        removed: t('pdfPageEditor.removed'),
        restore: t('pdfPageEditor.restore'),
        drag: t('pdfPageEditor.drag'),
    }), [t]);

    const openCrop = (page) => {
        setSaveMode(page.cropMode === 'flatten' ? 'flatten' : 'crop');
        setFlattenCorners(
            page.cropCorners?.length === 4
                ? page.cropCorners.map((c) => ({ ...c }))
                : defaultNormalizedCorners(),
        );
        setModalMode('crop');
        setModalPage(page);
    };
    const openView = (page) => { setModalMode('view'); setModalPage(page); };
    const openColor = (page) => {
        setDraftColor(normalizeColorAdjust(page));
        setModalMode('color');
        setModalPage(page);
    };

    const duplicatePage = (id) => onPagesChange((prev) => {
        const index = prev.findIndex((p) => p.id === id);
        if (index < 0) return prev;
        const copy = { ...prev[index], id: createToolItemId(), removed: false };
        const next = [...prev];
        next.splice(index + 1, 0, copy);
        return next;
    });

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        onPagesChange((prev) => {
            const oldIndex = prev.findIndex((p) => p.id === active.id);
            const newIndex = prev.findIndex((p) => p.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const rotateRight = (id) => onPagesChange((prev) => prev.map((page) => (
        page.id === id ? { ...page, rotation: (((page.rotation || 0) + 90) % 360) } : page
    )));

    const toggleRemoved = (id) => onPagesChange((prev) => prev.map((page) => (
        page.id === id ? { ...page, removed: !page.removed } : page
    )));

    const handleImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const saved = modalPage?.cropPct;
        if (saved) {
            setCrop(saved);
            setCompletedCrop(saved);
            return;
        }
        const next = centerCrop({ unit: '%', width: 80, height: 80 }, width, height);
        setCrop(next);
        setCompletedCrop(next);
    };

    const saveCrop = () => {
        if (!modalPage) return;

        if (saveMode === 'flatten') {
            const corners = flattenCorners?.length === 4 ? flattenCorners : defaultNormalizedCorners();
            const region = cornersToRegion(corners);
            if (!region) {
                notifications.show({ title: t('common.error'), message: t('pdfPageEditor.noRegion'), color: 'red' });
                return;
            }
            onPagesChange((prev) => prev.map((page) => (
                page.id === modalPage.id
                    ? {
                        ...page,
                        cropRegion: region,
                        cropPct: null,
                        cropCorners: corners,
                        cropMode: 'flatten',
                    }
                    : page
            )));
            setModalPage(null);
            return;
        }

        const region = percentCropToRegion(completedCrop);
        if (!region) {
            notifications.show({ title: t('common.error'), message: t('pdfPageEditor.noRegion'), color: 'red' });
            return;
        }
        onPagesChange((prev) => prev.map((page) => (
            page.id === modalPage.id
                ? {
                    ...page,
                    cropRegion: region,
                    cropPct: completedCrop,
                    cropCorners: null,
                    cropMode: 'crop',
                }
                : page
        )));
        setModalPage(null);
    };

    const clearCrop = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((page) => (
            page.id === modalPage.id
                ? { ...page, cropRegion: null, cropPct: null, cropCorners: null, cropMode: 'crop' }
                : page
        )));
        setModalPage(null);
    };

    const saveColor = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((page) => (
            page.id === modalPage.id
                ? { ...page, ...normalizeColorAdjust(draftColor) }
                : page
        )));
        setModalPage(null);
    };

    const clearColor = () => {
        if (!modalPage) return;
        onPagesChange((prev) => prev.map((page) => (
            page.id === modalPage.id
                ? { ...page, ...DEFAULT_COLOR_ADJUST }
                : page
        )));
        setModalPage(null);
    };

    return (
        <>
            <div className="h-full min-h-0 overflow-auto px-1 py-1 [scrollbar-width:thin]">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={pages.map((page) => page.id)} strategy={rectSortingStrategy}>
                        <div className={`grid gap-4 ${pages.length === 1 ? 'mx-auto max-w-sm grid-cols-1' : 'grid-cols-3'}`}>
                            {pages.map((page, index) => (
                                <PageCard
                                    key={page.id}
                                    page={{
                                        ...page,
                                        label: t('toolPage.pageNumber', {
                                            number: toBnDigits(page.sourcePageNumber ?? index + 1),
                                        }),
                                    }}
                                    thumb={thumbs[page.pageIndex]}
                                    flattenedSrc={flattenPreviews[page.id]}
                                    labels={labels}
                                    selectable={selectable}
                                    selected={selectable ? !!isSelected?.(page) : false}
                                    onToggleSelect={() => onToggleSelect?.(page)}
                                    onView={openView}
                                    onRotateRight={rotateRight}
                                    onCrop={openCrop}
                                    onColor={openColor}
                                    onDuplicate={duplicatePage}
                                    onToggleRemoved={toggleRemoved}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>

            {modalPage ? (
                <div
                    className="fixed inset-0 z-[200] flex h-dvh w-screen flex-col overflow-hidden bg-white dark:bg-zinc-900"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="flex flex-none items-center justify-between gap-3 border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-[#1C1815] dark:text-zinc-100">
                                    {t('toolPage.pageNumber', { number: toBnDigits(modalPage.sourcePageNumber) })}
                                </p>
                                <p className="truncate text-xs text-[#A8A099] dark:text-zinc-500">{file.name}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModalPage(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#ECE8E4] text-[#6B635C] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:text-zinc-300"
                                aria-label={t('common.close')}
                            >
                                <IconX size={16} stroke={2} />
                            </button>
                        </div>

                        <div
                            ref={stageRef}
                            className="flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden bg-[#1C1815] p-3 sm:p-5 dark:bg-zinc-950"
                        >
                            {loadingModal ? (
                                <div className="flex h-full items-center justify-center text-sm text-white/70">
                                    {t('pdfPageEditor.loadingPreview')}
                                </div>
                            ) : modalSrc ? (
                                <div className="flex min-h-0 min-w-0 items-center justify-center">
                                    {modalMode === 'crop' ? (
                                        saveMode === 'flatten' ? (
                                            <PerspectiveQuadCrop
                                                key={`${modalPage.id}-flatten`}
                                                src={modalSrc}
                                                value={flattenCorners}
                                                onChange={setFlattenCorners}
                                                className=""
                                                imgStyle={fitImageStyle(fitBox)}
                                            />
                                        ) : (
                                            <ReactCrop
                                                crop={crop}
                                                onChange={(next) => setCrop(next)}
                                                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                                                className="max-h-full max-w-full"
                                            >
                                                <img
                                                    ref={imgRef}
                                                    src={modalSrc}
                                                    alt=""
                                                    onLoad={handleImageLoad}
                                                    className="object-contain"
                                                    style={fitImageStyle(fitBox)}
                                                    draggable={false}
                                                />
                                            </ReactCrop>
                                        )
                                    ) : (
                                        <img
                                            src={modalSrc}
                                            alt=""
                                            className="object-contain shadow-[0_8px_28px_rgba(28,24,21,0.18)]"
                                            style={modalMode === 'color'
                                                ? { ...fitImageStyle(fitBox), filter: buildColorFilterCss(draftColor) }
                                                : fitImageStyle(fitBox)}
                                            draggable={false}
                                        />
                                    )}
                                </div>
                            ) : null}
                        </div>

                        {modalMode === 'crop' ? (
                            <div className="flex-none space-y-3 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t('pdfPageEditor.saveAs')}
                                    </span>
                                    <div className="flex gap-1 rounded-[11px] bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                        <button
                                            type="button"
                                            onClick={() => setSaveMode('crop')}
                                            className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                                                saveMode === 'crop'
                                                    ? 'bg-white text-pdf-cat-solid shadow-sm dark:bg-zinc-900 dark:text-orange-400'
                                                    : 'text-[#6B635C] dark:text-zinc-400'
                                            }`}
                                        >
                                            {t('pdfPageEditor.modeCrop')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSaveMode('flatten');
                                                setFlattenCorners((prev) => (
                                                    prev?.length === 4 ? prev : defaultNormalizedCorners()
                                                ));
                                            }}
                                            className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                                                saveMode === 'flatten'
                                                    ? 'bg-white text-pdf-cat-solid shadow-sm dark:bg-zinc-900 dark:text-orange-400'
                                                    : 'text-[#6B635C] dark:text-zinc-400'
                                            }`}
                                        >
                                            {t('pdfPageEditor.modeFlatten')}
                                        </button>
                                    </div>
                                    <p className="w-full text-[11px] text-[#A8A099] dark:text-zinc-500">
                                        {saveMode === 'flatten'
                                            ? t('pdfPageEditor.modeFlattenHint')
                                            : t('pdfPageEditor.modeCropHint')}
                                    </p>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        {modalPage.cropRegion ? (
                                            <button
                                                type="button"
                                                onClick={clearCrop}
                                                className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#D33A2C] transition-colors hover:border-red-200 dark:border-zinc-700 dark:bg-zinc-900"
                                            >
                                                {t('pdfPageEditor.removeCrop')}
                                            </button>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setModalPage(null)}
                                            className="rounded-xl border border-[#ECE8E4] bg-white px-4 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={saveCrop}
                                            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                                        >
                                            {t('common.save')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : modalMode === 'color' ? (
                            <PdfPageColorPanel
                                value={draftColor}
                                onChange={setDraftColor}
                                t={t}
                                onReset={clearColor}
                                onCancel={() => setModalPage(null)}
                                onSave={saveColor}
                                showReset={pageHasColorAdjust(draftColor) || pageHasColorAdjust(modalPage)}
                            />
                        ) : (
                            <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                                <button
                                    type="button"
                                    onClick={() => setModalPage(null)}
                                    className="rounded-xl border border-[#ECE8E4] bg-white px-5 py-2 text-sm font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </>
    );
}
